import type { Dispatch } from 'react';
import { SectionHeader } from '../../anketa/components/SectionHeader';
import type { QuoteAction } from '../quoteReducer';
import type { QuoteState } from '../types';

/**
 * Block 12 PR-D-A Phase D4 Step 5 — раздел «Стоимость» с критическим
 * margin helper (PRD §10.12.10:400) + mobile numeric keyboard + inline
 * validation.
 *
 * **Critical PRD UX:** mandatory helper text «Добавьте к сметной стоимости
 * свою маржу и укажите здесь.» (bold, 13px, color #7a6a5a). Без него
 * пользователь перенесёт сметную стоимость без наценки — это и есть
 * ключевая ошибка, которую секция предотвращает.
 *
 * **Pale yellow card style** (#fff8e8 + border YL `#f5c400`) per прототип
 * `screen-2.12.10-cp.html` lines 190-200 — отличается от default SectionCard
 * (white/orange). Inline-styled wrapper матчит прототип fidelity (Tailwind
 * design-tokens `#fff8e8` нет, ad-hoc оттенок).
 *
 * **Validation:** `cost` обязателен > 0 для Save (Phase D5 Step 11 enforce).
 * UI feedback inline: `aria-invalid` + error message «Введите положительное
 * число», но только если value уже введён (cost !== null) — иначе пустая
 * форма выглядела бы «броненой» с ошибкой.
 *
 * **Mobile keyboard:** `type="number"` + `inputMode="numeric"` гарантирует
 * numeric pad на Android/iOS.
 */
export interface SectionCostProps {
  state: QuoteState;
  dispatch: Dispatch<QuoteAction>;
}

const HELPER_STYLE = { fontSize: '13px', color: '#7a6a5a' } as const;
const CARD_STYLE = { background: '#fff8e8', borderColor: '#f5c400' } as const;

export function SectionCost({ state, dispatch }: SectionCostProps) {
  const cost = state.costWithMargin;
  const hasInput = cost !== null;
  const isValid = hasInput && cost > 0;
  const showError = hasInput && !isValid;

  const handleChange = (raw: string) => {
    if (raw === '') {
      dispatch({ type: 'SET_COST_WITH_MARGIN', value: null });
      return;
    }
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return;
    dispatch({ type: 'SET_COST_WITH_MARGIN', value: parsed });
  };

  return (
    <section
      data-testid="section-cost"
      className="mx-3 rounded-2xl border-[1.5px] shadow-card p-4"
      style={CARD_STYLE}
    >
      <SectionHeader className="mb-1.5">Стоимость</SectionHeader>

      <p className="mb-2.5 leading-tight" style={HELPER_STYLE} data-testid="cost-margin-helper">
        <strong>Добавьте к сметной стоимости свою маржу и укажите здесь.</strong>
      </p>

      <div className="relative">
        <input
          id="quote-cost-input"
          type="number"
          inputMode="numeric"
          min={0}
          step={1000}
          value={cost ?? ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Например, 2 500 000"
          aria-label="Стоимость для клиента"
          aria-invalid={showError}
          aria-describedby={showError ? 'quote-cost-error' : undefined}
          data-testid="cost-input"
          className="w-full pr-10 pl-3.5 rounded-xl border-[1.5px] border-cta bg-card text-ink text-2xl font-extrabold focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
          style={{ height: '64px' }}
        />
        <span
          aria-hidden="true"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted text-lg font-semibold pointer-events-none"
        >
          ₸
        </span>
      </div>

      {isValid && cost >= 1000 ? (
        <p
          className="mt-2 text-base font-semibold text-ink"
          data-testid="cost-formatted"
          aria-live="polite"
        >
          {cost.toLocaleString('ru-RU')} ₸
        </p>
      ) : null}

      {showError ? (
        <p
          id="quote-cost-error"
          role="alert"
          className="mt-1.5 text-sm text-destructive font-medium"
          data-testid="cost-error"
        >
          Введите положительное число
        </p>
      ) : null}
    </section>
  );
}
